import { useState, useEffect, useMemo } from 'react';
import i18n from 'i18next';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePaymentList } from 'hooks/usePayment';
import { useMeetingDetail } from 'hooks/useMeeting';
import { paymentStore } from 'store/paymentStore';
import { meetingStore } from 'store/meetingStore';
import { calculateSettlement } from 'common/utils/settlementCalculator';
import { shareCost, getCostShareUrl, copyToClipboard } from 'common/utils/share';
import {
    AppScreen, Badge, Button, ButtonFooter, InfoGrid, PageIntro, Section, SectionHeader,
} from 'components/ds';
import { IcChat, IcEnvelope, IcLink, IcClipboard } from 'components/common/icons/EmojiIcons';
import styles from './CostShare.module.css';

export const CostShare = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { meetingId } = useParams();
    const [copied, setCopied] = useState(false);
    const { data: paymentData } = usePaymentList();
    const { data: meetingData } = useMeetingDetail();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (meetingId) {
            paymentStore.onGetPaymentList(meetingId);
            meetingStore.onGetMeetingDetail(meetingId);
        }
    }, [meetingId]);

    const payments = paymentData?.data || [];
    const meeting = meetingData?.data;

    const settlement = useMemo(() => {
        return calculateSettlement(payments);
    }, [payments]);

    const resultData = {
        meetingName: meeting?.meetingName || meeting?.title || t('meeting.detail'),
        totalAmount: settlement.totalAmount,
        participants: settlement.participantCount,
        perPerson: settlement.perPersonAmount,
        transactions: settlement.transactions
    };

    const introsRaw = t('tone.shareCost.intro', { returnObjects: true }) as any;
    const closingsRaw = t('tone.shareCost.closing', { returnObjects: true }) as any;
    const introsArr: string[] = Array.isArray(introsRaw) ? introsRaw : [`"${resultData.meetingName}"`];
    const closingsArr: string[] = Array.isArray(closingsRaw) ? closingsRaw : [''];
    const intro = introsArr[Math.floor(Math.random() * introsArr.length)].replace(/\{\{name\}\}/g, resultData.meetingName);
    const closing = closingsArr[Math.floor(Math.random() * closingsArr.length)];
    const totalLabel = t('tone.shareCost.totalLabel');
    const perPersonLabel = t('tone.shareCost.perPersonLabel');
    const transactionsLabel = t('tone.shareCost.transactionsLabel');

    const shareText = `${intro}

${totalLabel}: ${resultData.totalAmount.toLocaleString()}
${perPersonLabel}: ${resultData.perPerson.toLocaleString()}

${transactionsLabel}
${resultData.transactions.map((tx: any) =>
    `  ${tx.from} → ${tx.to}: ${tx.amount.toLocaleString()}`
).join('\n')}${closing}`;

    const handleCopyText = async () => {
        await copyToClipboard(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleKakaoShare = () => {
        shareCost({
            meetingName: resultData.meetingName,
            totalAmount: resultData.totalAmount,
            perPerson: resultData.perPerson,
            shareText,
            meetingId: meetingId || '',
        });
    };

    const handleSMSShare = () => {
        shareCost({
            meetingName: resultData.meetingName,
            totalAmount: resultData.totalAmount,
            perPerson: resultData.perPerson,
            shareText,
            meetingId: meetingId || '',
        });
    };

    const handleLinkShare = async () => {
        const shareUrl = getCostShareUrl(meetingId || '');
        await copyToClipboard(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDone = () => {
        navigate(`/meeting/detail/${meetingId}`, { replace: true });
    };

    const won = (n: number) => t('home.wonAmount', { amount: n.toLocaleString('ko-KR') });

    const shareWays = [
        { id: 'kakao', label: t('costComp.kakao'), icon: <IcChat size={20} />, onClick: handleKakaoShare },
        { id: 'sms', label: t('costComp.sms'), icon: <IcEnvelope size={20} />, onClick: handleSMSShare },
        { id: 'link', label: t('costComp.copyLink'), icon: <IcLink size={20} />, onClick: handleLinkShare },
        { id: 'text', label: t('costComp.copyText'), icon: <IcClipboard size={20} />, onClick: handleCopyText },
    ];

    return (
        <AppScreen title={i18n.t('costShare.shareTitle')} bar="close" hasFooter>
            <PageIntro
                title={i18n.t('costShare.settleDone')}
                desc={t('costShare.shareNow')}
            />

            {/* 정산 결과 */}
            <Section spacing="tight">
                <div className={styles.head}>
                    <span className={styles.meeting}>{resultData.meetingName}</span>
                    <Badge tone="brand">{t('costComp.settleCompleteBadge')}</Badge>
                </div>
                <InfoGrid
                    items={[
                        { label: t('costComp.totalSpent'), value: won(resultData.totalAmount) },
                        { label: t('costComp.perPerson'), value: won(resultData.perPerson) },
                    ]}
                />
            </Section>

            {/* 누가 누구에게 */}
            {resultData.transactions.length > 0 && (
                <Section divided>
                    <SectionHeader
                        title={t('costComp.settlementDetail')}
                        count={resultData.transactions.length}
                    />
                    <ul className={styles.rows}>
                        {resultData.transactions.map((tx: any, index: number) => (
                            <li key={index} className={styles.row}>
                                <span className={styles.rowWho}>{tx.from}</span>
                                <span className={styles.rowArrow} aria-hidden>→</span>
                                <span className={styles.rowWho}>{tx.to}</span>
                                <span className={styles.rowAmount}>{won(tx.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {/* 어떻게 보낼지 */}
            <Section divided>
                <SectionHeader title={t('costComp.shareMethod')} />
                <div className={styles.ways}>
                    {shareWays.map(w => (
                        <button key={w.id} type="button" className={styles.way} onClick={w.onClick}>
                            <span className={styles.wayIcon}>{w.icon}</span>
                            <span className={styles.wayLabel}>{w.label}</span>
                        </button>
                    ))}
                </div>
                {copied && <p className={styles.copied}>{t('costComp.copiedToast')}</p>}
            </Section>

            {/* 보낼 내용 */}
            <Section divided last>
                <SectionHeader title={i18n.t('costShare.shareContent')} />
                <pre className={styles.preview}>{shareText}</pre>
                <p className={styles.notice}>{t('costShare.noticeSent')}</p>
            </Section>

            <ButtonFooter>
                <Button variant="primary" size="lg" block onClick={handleDone}>
                    {t('common.done')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
